import { DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@stereograph/teia-system-design/components/Button';
import { Icon } from '@stereograph/teia-system-design/components/Icon';
import { Stack } from '@stereograph/teia-system-design/components/Stack';
import CodeMirror, { EditorView } from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { jsonSchema } from 'codemirror-json-schema';
import { KeyboardEvent, RefObject, useRef } from 'react';
import type { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { Box } from '@stereograph/teia-system-design/components/Box';
import { SxProps } from '@mui/material/styles';

export interface FlowConfigEditorProps {
  sx?: SxProps;
  flowValue: string;
  onChange: (value: string) => void;
  schema?: any;
  flowErrors: string;
  flowIsValid: boolean;
}

export const FlowEditor = (props: FlowConfigEditorProps) => {
  const { flowValue, onChange, sx, schema, flowErrors, flowIsValid } = props;

  const ref = useRef<ReactCodeMirrorRef>(null);
  const onCodeChange = (value: string) => {
    onChange(value);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    e.stopPropagation();
  };

  const onFormat = () => {
    const data = JSON.parse(flowValue);
    onChange(JSON.stringify(data, null, 2));
  };

  return (
    <Box
      sx={{
        ...sx,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'scroll',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        {!flowIsValid && SolveErrorsButton(ref)}
        <Button onClick={onFormat} aria-label="Format" color="info" variant="text">
          <Icon component={DocumentTextIcon} />
          Format JSON
        </Button>
      </Box>
      <Stack
        spacing={{
          xs: 2,
        }}
      >
        <CodeMirror
          style={{
            border: '1px solid lightgray',
            height: '100%',
            width: '100%',
          }}
          tabIndex={0}
          theme="light"
          value={flowValue}
          onChange={onCodeChange}
          onKeyDown={onKeyDown}
          extensions={[json(), jsonSchema(schema), EditorView.lineWrapping]}
          basicSetup={{
            foldGutter: true,
            dropCursor: false,
            allowMultipleSelections: true,
            indentOnInput: true,
            autocompletion: true,
            bracketMatching: true,
            closeBrackets: true,
          }}
        />
        {!flowIsValid && SolveErrorsEditor(flowErrors, ref)}
      </Stack>
    </Box>
  );
};

const SolveErrorsEditor = (flowErrors: string, ref: RefObject<ReactCodeMirrorRef>) => {
  return (
    <CodeMirror
      style={{
        border: '1px solid lightgray',
        height: '100%',
        width: '100%',
      }}
      tabIndex={0}
      theme="light"
      value={flowErrors}
      extensions={[json(), EditorView.lineWrapping]}
      basicSetup={{
        foldGutter: true,
      }}
      editable={false}
      readOnly={true}
      ref={ref}
    />
  );
};

const SolveErrorsButton = (ref: RefObject<ReactCodeMirrorRef>) => {
  const { current } = ref;
  const onSolveErrors = () => {
    current?.editor?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  };

  return (
    <Button onClick={onSolveErrors} aria-label="Solver Errors" color="error" variant="text">
      <Icon component={ExclamationTriangleIcon} />
      Solve errors
    </Button>
  );
};
